import AuthenticatedUserContext from "@/contexts/authentication/AuthenticatedUserContext";
import { createSentryAnonymousUser } from "@/errors";
import { Alert, alertClasses, AlertTitle, Button, Collapse, Grid, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { Link, useRouteError } from "react-router-dom";

const RouteErrorBoundary : FC = () => {
    const error = useRouteError() as Error;

    const { data: user, loading: userLoading, validating: userValidating } = useContext(AuthenticatedUserContext);

    const errorLogged = useRef(false);

    useEffect(() => {
        if (userLoading || userValidating) {
            return;
        }

        if (errorLogged.current) {
            return;
        }

        const scope =  Sentry.getCurrentScope();
        const sentryUser = Sentry.getCurrentScope().getUser();

        if (user && (!sentryUser || sentryUser.id !== user.userName)) {
            scope.setUser({
                id: user.userName,
                username: user.userName,
                fullName: user.displayName,
                authenticated: true
            })
        }

        if (!user && sentryUser && sentryUser.authenticated) {
            scope.setUser(createSentryAnonymousUser())
        }

        Sentry.captureException(error);

        errorLogged.current = true;

    }, [error, userLoading, userValidating, errorLogged]);
    
    const [details, setDetails] = useState(false);

    return (
        <>
            <Alert
                sx={{
                    margin: 2,
                    [`& .${alertClasses.message}`]: {
                        width: "100%"
                    }
                }}
                severity="error"
            >
                <AlertTitle>An error has occurred!</AlertTitle>
                <Typography gutterBottom>
                    Please refresh the page and try again.
                </Typography>
                {
                    error?.message && (
                        <Typography gutterBottom>
                            <strong>Error:</strong> <em>{error.message}</em>
                        </Typography>
                    )
                }
                <Grid
                    container
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        width: "100%"
                    }}
                    gap={2}
                >
                    <Grid item>
                        {
                            error?.stack && (
                                <>
                                    <Button onClick={() => setDetails(c => !c)}>
                                        More Details
                                    </Button>
                                </>
                            )
                        }
                    </Grid>
                    <Grid item>
                        <Button
                            component={Link}
                            to={"/"}
                            variant="contained"
                        >
                            Home
                        </Button>
                    </Grid>
                </Grid>
                {
                    error?.stack && (
                    <Collapse in={details}>
                        <pre>
                            {error.stack}
                        </pre>
                    </Collapse>
                    )
                }
            </Alert>
        </>
    );
}

export default RouteErrorBoundary;